// Array Footman (replaces `drone`) — basic swarm soldier of the Severed Guard (P1 §2/01, elite §4: Gilded Centurion).
// Thin armoured humanoid: narrow ivory helmet with a dim teal eye slit, squared ivory shoulders, dark navy-iron body,
// ivory greaves and short broken-ceramic blades in both hands. Quick forward-leaning march with counter-swinging arms.
// Wind-up: the right blade lifts high, the left guards forward.
import * as THREE from 'three/webgpu';
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL, plate } from './kit';
import { shard, sideways, slab } from './parts_swarm';

type V3 = [number, number, number];
const HIP_Y = 0.92;
const LEAN = 0.13; // forward lean of the upper body (rad)
const LEG_SWING = 0.55, ARM_SWING = 0.5;

// upper body is authored upright, then leaned forward about the hip
const LM = new THREE.Matrix4().makeTranslation(0, HIP_Y, 0).multiply(new THREE.Matrix4().makeRotationX(LEAN)).multiply(new THREE.Matrix4().makeTranslation(0, -HIP_Y, 0));
const L = (g: THREE.BufferGeometry) => g.applyMatrix4(LM);
const Lp = (p: V3): V3 => { const v = new THREE.Vector3(...p).applyMatrix4(LM); return [v.x, v.y, v.z]; };

/** short broken-ceramic blade, authored in the YZ plane pointing +Z from the grip at the origin */
function blade(len: number): THREE.BufferGeometry {
  return sideways(slab([[-0.04, -0.035], [len * 0.55, -0.05], [len * 0.8, -0.02], [len * 0.72, 0.01], [len, 0.03], [len * 0.62, 0.055], [-0.04, 0.04]], 0.03));
}

function build(elite: boolean): ArtGeo {
  const b = new ArtBuilder();
  const armour = PAL.ivory, body = 0x323a52, dark = PAL.iron; // steel-navy body (kept distinct from the hero's suit)

  // ---- legs (whole leg swings about the hip; authored with a slight knee bend so the stride reads)
  for (const s of [-1, 1]) {
    const leg = { pivot: [s * 0.11, HIP_Y, 0] as V3, swing: LEG_SWING, sphase: s > 0 ? 0 : Math.PI };
    const hip: V3 = [s * 0.11, HIP_Y, 0], knee: V3 = [s * 0.125, 0.5, 0.06], ankle: V3 = [s * 0.13, 0.11, -0.02];
    b.add(limb(hip, knee, 0.085, 0.068), { color: body, ...leg });
    b.add(limb(knee, ankle, 0.075, 0.052), { color: armour, ...leg }); // ivory greave
    b.add(at(plate(0.13, 0.1, 0.13, 0.03, 1), s * 0.125, 0.52, 0.1, 0.25), { color: PAL.ivoryShade, ...leg }); // knee guard
    b.add(at(plate(0.13, 0.1, 0.28, 0.035, 1), s * 0.13, 0.05, 0.04), { color: dark, ...leg }); // boot
  }

  // ---- hips and torso (leaned)
  b.add(L(at(plate(0.34, 0.17, 0.22, 0.05, 1), 0, HIP_Y + 0.02, 0)), { color: dark });
  b.add(L(at(plate(0.22, 0.2, 0.05, 0.02, 1), 0, HIP_Y - 0.05, 0.12, -0.12)), { color: PAL.copperDark }); // front tasset
  b.add(L(limb([0, HIP_Y + 0.05, 0], [0, 1.2, 0], 0.13, 0.16, 6)), { color: dark }); // waist
  b.add(L(at(plate(0.42, 0.3, 0.26, 0.06, 1), 0, 1.3, 0.0)), { color: body }); // chest
  b.add(L(at(plate(0.24, 0.16, 0.05, 0.03, 1), 0, 1.31, 0.13)), { color: elite ? PAL.gold : PAL.ironLight }); // breastplate
  b.add(L(at(new THREE.OctahedronGeometry(0.035), 0, 1.31, 0.165, 0, 0, 0, [1, 1.4, 0.6])), { color: PAL.teal, glow: true }); // damaged core
  b.add(L(limb([0, 1.42, 0], [0, 1.52, 0.02], 0.06, 0.055, 6)), { color: dark }); // neck

  // ---- narrow helmet with eye slit
  b.add(L(at(new THREE.SphereGeometry(1, 10, 7), 0, 1.62, 0.02, 0, 0, 0, [0.12, 0.155, 0.16])), { color: armour });
  b.add(L(at(plate(0.2, 0.06, 0.22, 0.025, 1), 0, 1.53, 0.0)), { color: PAL.ivoryShade }); // jaw guard / gorget
  b.add(L(at(plate(0.16, 0.055, 0.06, 0.02, 1), 0, 1.615, 0.135)), { color: dark }); // visor band
  b.add(L(at(plate(0.13, 0.022, 0.02, 0.008, 1), 0, 1.618, 0.168)), { color: PAL.teal, glow: true }); // eye slit
  if (elite) {
    // tall swept crest (gold) with a red fin
    b.add(L(at(sideways(slab([[0.16, 0], [0.05, 0.2], [-0.12, 0.26], [-0.32, 0.14], [-0.2, 0.08], [-0.1, 0.0]], 0.05)), 0, 1.72, 0)), { color: PAL.gold });
    b.add(L(at(sideways(slab([[0.03, 0.17], [-0.1, 0.22], [-0.26, 0.13], [-0.1, 0.12]], 0.07)), 0, 1.72, 0)), { color: PAL.red });
  } else {
    b.add(L(at(plate(0.035, 0.06, 0.26, 0.012, 1), 0, 1.76, 0.0)), { color: PAL.ivoryShade }); // helmet ridge
  }

  // ---- squared ivory shoulders (ride the torso)
  for (const s of [-1, 1]) {
    b.add(L(at(plate(0.23, 0.12, 0.28, 0.045, 1), s * 0.31, 1.45, 0, 0, 0, -s * 0.28)), { color: armour });
    if (elite) {
      // reinforced: a second raised layer and a gold rim
      b.add(L(at(plate(0.24, 0.08, 0.3, 0.035, 1), s * 0.34, 1.5, 0, 0, 0, -s * 0.42)), { color: armour });
      b.add(L(at(plate(0.06, 0.12, 0.31, 0.02, 1), s * 0.45, 1.4, 0, 0, 0, -s * 0.1)), { color: PAL.gold });
    }
  }

  // ---- arms with blades (whole arm swings about the shoulder, counter to the same-side leg)
  for (const s of [-1, 1]) {
    const sh: V3 = [s * 0.29, 1.38, 0], el: V3 = [s * 0.33, 1.1, -0.04], wr: V3 = [s * 0.32, 0.92, 0.1];
    const arm = { pivot: Lp(sh), swing: ARM_SWING, sphase: s > 0 ? Math.PI : 0, raise: s > 0 ? -1.75 : -0.7 };
    b.add(L(limb(sh, el, 0.065, 0.055)), { color: body, ...arm });
    b.add(L(limb(el, wr, 0.062, 0.05)), { color: PAL.ironLight, ...arm });
    b.add(L(at(new THREE.SphereGeometry(0.045, 6, 3), el[0], el[1], el[2])), { color: PAL.copper, ...arm }); // elbow joint
    b.add(L(at(plate(0.09, 0.09, 0.1, 0.025, 1), wr[0], wr[1] - 0.02, wr[2] + 0.03)), { color: dark, ...arm }); // fist
    const bl = blade(elite ? 0.55 : 0.46);
    b.add(L(at(bl, wr[0], wr[1] - 0.03, wr[2] + 0.05, -0.15)), { color: elite && s > 0 ? PAL.gold : 0xf2ead8, ...arm });
  }

  // ---- elite: fractured gold standard on the back
  if (elite) {
    const base: V3 = [0, 1.05, -0.17], top: V3 = [0, 2.35, -0.24];
    b.add(L(limb(base, top, 0.026, 0.022, 6)), { color: PAL.copperDark });
    b.add(L(limb([-0.24, 2.2, -0.23], [0.24, 2.2, -0.23], 0.02, 0.02, 6)), { color: PAL.copperDark });
    // the banner is split by a diagonal fracture into two gold pieces with a gap
    b.add(L(at(slab([[-0.22, 0], [0.22, 0], [0.22, -0.18], [-0.05, -0.32], [-0.22, -0.24]], 0.025), 0, 2.18, -0.23)), { color: PAL.gold });
    b.add(L(at(slab([[-0.22, -0.3], [-0.03, -0.38], [0.22, -0.24], [0.22, -0.5], [0.08, -0.42], [-0.08, -0.56], [-0.22, -0.46]], 0.025), 0, 2.16, -0.23)), { color: PAL.amber });
    b.add(L(shard([0, 2.35, -0.24], [0, 2.56, -0.25], 0.05, 4)), { color: PAL.teal, glow: true });
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Array Footman', build, stride: 3.3, bob: -0.11, outline: 0.03 };
